import { matchroomLog } from '@/lib/matchroom-logger';

type LogContext = Record<string, unknown>;

/** Media pipeline logger — filter console by `[MatchRoom]`. */
export const mediaLogger = {
  boot() {
    matchroomLog.boot();
  },
  gate(message: string, context?: LogContext) {
    matchroomLog.peer(message, context);
  },
  requestingMedia(context?: LogContext) {
    matchroomLog.capture('requesting media', context);
  },
  mediaGranted(context: { audioTracks: number; videoTracks: number; trackIds: string[] }) {
    matchroomLog.capture('media granted', context);
  },
  streamCreated(context: { streamId: string; trackIds: string[] }) {
    matchroomLog.capture('stream created', context);
  },
  streamAttached(context: { target: string; streamId?: string } & LogContext) {
    matchroomLog.render('stream attached', context);
  },
  peerInit(context: { roomId: string; userId: string }) {
    matchroomLog.peer('initialized', context);
  },
  iceInit(context: { peerUserId: string; signalingState?: string }) {
    matchroomLog.ice('initialized', context);
  },
  localStreamStopped(reason: string, context?: LogContext) {
    matchroomLog.warn(`local stream stopped: ${reason}`, context);
  },
  permissionChanged(context: LogContext) {
    matchroomLog.permission('state changed', context);
  },
  sessionAcquired(context: { roomId: string; userId: string; holders: number; reused: boolean }) {
    matchroomLog.room('session acquired', context);
  },
  sessionReleased(context: { roomId: string; userId: string; holders: number; scheduledLeave: boolean }) {
    matchroomLog.room('session released', context);
  },
  joinComplete(context: { roomId: string; userId: string; participantCount: number }) {
    matchroomLog.room('join complete', context);
  },
  joinFailed(stage: string, err: unknown, context?: LogContext) {
    matchroomLog.error(`join failed at ${stage}`, err, context);
  },
  retry(context: { mediaRetryKey: number }) {
    matchroomLog.peer('media retry requested', context);
  },
  videoRender(context: LogContext) {
    matchroomLog.render('video state', context);
  },
  signaling(context: LogContext) {
    matchroomLog.signaling('event', context);
  },
  remoteTrack(context: LogContext) {
    matchroomLog.track('remote stream updated', context);
  },
  capture(context: LogContext) {
    matchroomLog.capture('tracks ready', context);
  },
  publish(context: LogContext) {
    matchroomLog.publish('outbound update', context);
  },
  ontrack(context: LogContext) {
    matchroomLog.track('received', context);
  },
  screenShareStarted(context: LogContext) {
    matchroomLog.capture('screen share started', context);
  },
  screenTrackReplaced(context: LogContext) {
    matchroomLog.publish('screen track replaced', context);
  },
  screenShareStopped(context: LogContext) {
    matchroomLog.capture('screen share stopped', context);
  },
  cameraRestored(context: LogContext) {
    matchroomLog.capture('camera restored', context);
  },
};

mediaLogger.boot();
